import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import "../css/ServiceLocation.css";
import { ShimmerBox } from "../components/Shimmer";
import "../css/Shimmer.css";
import AddressInput from "../components/AddressInput";
import {
  searchIcon,
  totalDriverIcon,
  completedJobsIcon,
  avDriverRatingIcon,
  bulb_icon,
  BaseLocation,
  BaseNameIcon,
  AddressBlackPin,
  BaseEditIcon,
  BaseDeleteIcon,
} from "../assets";
import {
  getBaseLocations,
  createBaseLocation,
  updateBaseLocation,
  deleteBaseLocation,
  getAllServices,
  clearError,
} from "../features/serviceLocations/serviceLocationSlice";
import pauseConfirmIcon from "../assets/images/Formicons/pause_confirm.svg";

// ─── Icons ──────────────────────────────────────────────────────────────────
const AddIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 3V15M3 9H15"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M7.5 5L12.5 10L7.5 15"
      stroke="#6366F1"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Page Loading Skeleton ───────────────────────────────────────────────────
const PageLoadingSkeleton = () => (
  <div className="sl-page-skeleton sl-shimmer-scope">
    <div className="sl-skeleton-stats">
      {[1, 2, 3].map((i) => (
        <ShimmerBox key={i} width="100%" height="88px" borderRadius="12px" />
      ))}
    </div>
    <div className="sl-skeleton-header">
      <ShimmerBox width="160px" height="28px" borderRadius="8px" />
      <ShimmerBox width="220px" height="40px" borderRadius="8px" />
      <ShimmerBox width="130px" height="40px" borderRadius="8px" />
    </div>
    <ShimmerBox
      width="100%"
      height="56px"
      borderRadius="10px"
      style={{ marginBottom: "24px" }}
    />
    {[1, 2, 3].map((i) => (
      <ShimmerBox
        key={i}
        width="100%"
        height="72px"
        borderRadius="12px"
        style={{ marginBottom: "12px" }}
      />
    ))}
  </div>
);

// ─── Empty Location State ────────────────────────────────────────────────────
const EmptyLocationState = ({ allServices, isLoadingServices, onAddClick }) => {
  const steps = [
    {
      number: "Step 1",
      title: "Add a location",
      description: "Enter your business address and service area radius",
    },
    {
      number: "Step 2",
      title: "Enable services",
      description:
        "Choose from our catalog – car wash, EV charging, tire repair, and more",
    },
    {
      number: "Step 3",
      title: "Set pricing & go live",
      description:
        "Configure variants, pricing, availability – and start accepting your bookings",
    },
  ];

  return (
    <div className="sl-empty-state">
      <div className="sl-empty-hero">
        <div className="sl-empty-pin">
          <img src={BaseLocation} alt="Location" className="sl-empty-pin-icon" />
        </div>
        <h2 className="sl-empty-hero-title">Set up your first location</h2>
        <p className="sl-empty-hero-desc">
          Add where you operate, pick which services you offer, set
          <br />
          your pricing – and you're live. Takes about 5 minutes.
        </p>
        <button className="sl-empty-cta" onClick={onAddClick}>
          Add Your First Location
        </button>
      </div>

      <div className="sl-empty-steps">
        {steps.map((step, i) => (
          <div key={i} className="sl-empty-step-card">
            <div className="sl-empty-step-header">
              <div className="sl-empty-step-icon">
                <img
                  src={BaseLocation}
                  alt={step.number}
                  className="sl-empty-step-img"
                />
              </div>
              <span className="sl-empty-step-number">{step.number}</span>
            </div>
            <h3 className="sl-empty-step-title">{step.title}</h3>
            <p className="sl-empty-step-desc">{step.description}</p>
          </div>
        ))}
      </div>

      <div className="sl-empty-catalog">
        <h3 className="sl-empty-catalog-title">Services in Catalog</h3>
        <div className="sl-empty-catalog-chips">
          {isLoadingServices
            ? [1, 2, 3, 4, 5].map((i) => (
                <ShimmerBox key={i} width="140px" height="40px" borderRadius="8px" />
              ))
            : allServices.map((svc) => (
                <div key={svc.ServiceId} className="sl-empty-catalog-chip">
                  <img
                    src={svc.Icon}
                    alt={svc.ServiceName}
                    className="sl-empty-chip-icon"
                  />
                  <span>{svc.ServiceName}</span>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

// ─── Add / Edit Location Modal ───────────────────────────────────────────────
const LocationModal = ({
  mode,
  location,
  allServices,
  isLoadingServices,
  isSubmitting,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState({
    BaseName: location?.BaseName || "",
    Address: location?.Address || "",
    // City, State, Pincode, Lat, Lng are auto-populated from AddressInput
    // Google suggestion — not entered manually.
    City: location?.City || "",
    State: location?.State || "",
    Pincode: location?.Pincode || "",
    Latitude: location?.Latitude || "",
    Longitude: location?.Longitude || "",
    ServiceIds: location?.Services?.map((s) => s.ServiceId) || [],
  });

  // ─── serviceIdsRef: targeted fix for stale-closure on ServiceIds only ──
  //
  // Problem: when the services dropdown is open and the user clicks Save,
  // the browser fires mousedown first (which triggers setServicesOpen(false)
  // causing a React re-render), then fires click (which calls handleSave).
  // If React processes the re-render between those two events, handleSave
  // captures `form` from the OLD closure — ServiceIds may be stale.
  //
  // Fix: keep a ref that's updated synchronously inside the setForm updater
  // specifically for ServiceIds. All other fields (BaseName, Address, etc.)
  // are reliable in the React `form` state because they're updated by simple
  // text inputs or AddressInput callbacks — no competing re-render involved.
  const serviceIdsRef = useRef(location?.Services?.map((s) => s.ServiceId) || []);

  const [servicesOpen, setServicesOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const dropdownRef = useRef(null);
  const modalBodyRef = useRef(null);

  // Close services dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setServicesOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ESC key + lock body scroll
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  const validate = (f) => {
    const e = {};
    if (!f.BaseName?.trim()) e.BaseName = "Base name is required.";
    if (!f.Address?.trim()) e.Address = "Address is required.";
    return e;
  };

  const handleSave = () => {
    setServicesOpen(false);

    // Use React `form` state for all fields — it's reliable for text/address fields.
    // For ServiceIds specifically, use serviceIdsRef which is kept in sync
    // synchronously inside the setForm updater, avoiding the stale-closure
    // issue when the services dropdown is open while clicking Save.
    const latestForm = {
      ...form,
      ServiceIds: serviceIdsRef.current,
    };

    const e = validate(latestForm);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    onSave(latestForm);
  };

  const toggleService = (id) => {
    setForm((prev) => {
      const newServiceIds = prev.ServiceIds.includes(id)
        ? prev.ServiceIds.filter((s) => s !== id)
        : [...prev.ServiceIds, id];
      // Update ref synchronously inside the updater so handleSave always
      // sees the latest value regardless of React's render schedule.
      serviceIdsRef.current = newServiceIds;
      return { ...prev, ServiceIds: newServiceIds };
    });
  };

  const selectedLabel =
    form.ServiceIds.length === 0
      ? "Choose services"
      : `${form.ServiceIds.length} service${form.ServiceIds.length > 1 ? "s" : ""} selected`;

  // Base Name text field
  const baseNameField = () => (
    <div className="sl-form-group">
      <label className="sl-form-label">Base Name</label>
      <div className="sl-input-wrapper">
        <span className="sl-input-icon">
          <img src={BaseNameIcon} alt="Base Name" width="18" height="18" />
        </span>
        <input
          type="text"
          className={`sl-form-input sl-form-input--icon${errors.BaseName ? " sl-form-input--error" : ""}`}
          placeholder="e.g. Westwood Garage"
          value={form.BaseName}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, BaseName: e.target.value }));
            if (errors.BaseName)
              setErrors((prev) => { const n = { ...prev }; delete n.BaseName; return n; });
          }}
        />
      </div>
      {errors.BaseName && (
        <span className="sl-field-error">{errors.BaseName}</span>
      )}
    </div>
  );

  return (
    <div
      className="sl-modal-overlay"
      onClick={() => { setServicesOpen(false); onClose(); }}
    >
      <div  className="sl-modal" 
  onClick={(e) => e.stopPropagation()}
  onMouseDown={(e) => e.stopPropagation()}  >

        {/* Header */}
        <div className="sl-modal-header">
          <div className="sl-modal-header-left">
            <div className="sl-modal-pin-icon">
              <img src={BaseLocation} alt="Base" className="sl-base-icon" />
            </div>
            <h3 className="sl-modal-title">
              {mode === "add" ? "Add Location" : "Edit Location"}
            </h3>
          </div>
          <button className="sl-modal-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 3L13 13M13 3L3 13"
                stroke="#6366F1"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="sl-modal-body" ref={modalBodyRef}>

          {/* Base Name */}
          {baseNameField()}

          {/* Address — uses AddressInput (same as ProfileSettings).
              Google suggestions appear on typing; selecting one auto-fills
              City, State, Pincode, Latitude, Longitude in form state
              (not shown as manual inputs). */}
          <div className="sl-form-group">
            <label className="sl-form-label">Address</label>
            <AddressInput
              value={form.Address}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, Address: e.target.value }));
                if (errors.Address)
                  setErrors((prev) => { const n = { ...prev }; delete n.Address; return n; });
              }}
              onBlur={() => {}}
              placeholder="Search for address…"
              name="address"
              icon={AddressBlackPin}
              disabled={false}
              onLocationChange={(locationData) => {
                // Auto-populated from Google — no manual input needed
                setForm((prev) => ({
                  ...prev,
                  City: locationData.city || prev.City,
                  State: locationData.state || prev.State,
                  Pincode: locationData.pincode || prev.Pincode,
                  Latitude: locationData.latitude?.toString() || prev.Latitude,
                  Longitude: locationData.longitude?.toString() || prev.Longitude,
                }));
              }}
            />
            {errors.Address && (
              <span className="sl-field-error">{errors.Address}</span>
            )}
          </div>

          {/* Services Dropdown */}
          <div className="sl-form-group" ref={dropdownRef}>
            <label className="sl-form-label">Services</label>
            <button
              className="sl-services-trigger"
              type="button"
              disabled={isLoadingServices}
              onClick={() => {
                const newOpen = !servicesOpen;
                setServicesOpen(newOpen);
                if (newOpen) {
                  setTimeout(() => {
                    modalBodyRef.current?.scrollTo({
                      top: modalBodyRef.current.scrollHeight,
                      behavior: "smooth",
                    });
                  }, 50);
                }
              }}
            >
              <span
                className={
                  form.ServiceIds.length === 0
                    ? "sl-services-placeholder"
                    : "sl-services-value"
                }
              >
                {isLoadingServices ? "Loading services…" : selectedLabel}
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: servicesOpen ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="#94a3b8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div
              className={`sl-services-list${servicesOpen ? " sl-services-list--open" : ""}`}
            >
              {allServices.map((svc) => {
                const checked = form.ServiceIds.includes(svc.ServiceId);
                return (
                  <label
                    key={svc.ServiceId}
                    className="sl-service-checkbox-row"
                    onClick={() => toggleService(svc.ServiceId)}
                  >
                    <div
                      className={`sl-checkbox${checked ? " sl-checkbox--checked" : ""}`}
                    >
                      {checked && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="#fff"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="sl-checkbox-info">
                      <span className="sl-checkbox-label">{svc.ServiceName}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="sl-modal-footer">
          <button
            className="sl-btn-cancel"
            onClick={() => { setServicesOpen(false); onClose(); }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="sl-btn-save"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? mode === "add" ? "Adding…" : "Saving…"
              : mode === "add" ? "Add Location" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Service Tag Row ──────────────────────────────────────────────────────────
const ServiceTagRow = ({ services, className = "" }) => {
  if (!services?.length) return null;
  return (
    <div className={`sl-service-tags ${className}`}>
      {services.map((svc) => (
        <span
          key={svc.ServiceId}
          className={`sl-service-tag ${svc.IsEnabled ? "sl-service-tag--active" : "sl-service-tag--inactive"}`}
        >
          <img src={svc.Icon} alt={svc.ServiceName} className="sl-tag-icon" />
          {svc.ServiceName}
          <span
            className="sl-tag-dot"
            style={{ background: svc.IsEnabled ? "#10B981" : "#CBD5E1" }}
          />
        </span>
      ))}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const ServiceLocation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    bases,
    summary,
    allServices,
    isLoading,
    isCreating,
    isUpdating,
    isLoadingServices,
    error,
  } = useSelector((state) => state.serviceLocations);

  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editBase, setEditBase] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    dispatch(getBaseLocations());
  }, [dispatch]);

  useEffect(() => {
    if (allServices.length === 0) dispatch(getAllServices());
  }, [allServices.length, dispatch]);

  useEffect(() => {
    if ((showAddModal || editBase) && allServices.length === 0)
      dispatch(getAllServices());
  }, [showAddModal, editBase, allServices.length, dispatch]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const result = await dispatch(deleteBaseLocation(deleteTarget.BaseId));
    if (deleteBaseLocation.fulfilled.match(result)) {
      setDeleteTarget(null);
      showToast("success", `"${deleteTarget.BaseName}" deleted successfully.`);
    }
  };

  useEffect(() => {
    if (error) {
      showToast("error", error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const filteredBases = bases.filter((base) => {
    const q = searchQuery.toLowerCase();
    return (
      base.BaseName?.toLowerCase().includes(q) ||
      base.Address?.toLowerCase().includes(q) ||
      base.City?.toLowerCase().includes(q) ||
      base.Services?.some((s) => s.ServiceName?.toLowerCase().includes(q))
    );
  });

  const handleAddSave = async (form) => {
    const result = await dispatch(
      createBaseLocation({
        BaseName: form.BaseName,
        Address: form.Address,
        City: form.City,
        State: form.State,
        Pincode: form.Pincode,
        Latitude: form.Latitude || "0",
        Longitude: form.Longitude || "0",
        ServiceIds: form.ServiceIds,
      }),
    );
    if (createBaseLocation.fulfilled.match(result)) {
      setShowAddModal(false);
      showToast("success", "Location added successfully.");
    }
  };

  const handleEditSave = async (form) => {
    const result = await dispatch(
      updateBaseLocation({
        baseId: editBase.BaseId,
        data: {
          BaseName: form.BaseName,
          Address: form.Address,
          City: form.City,
          State: form.State,
          Pincode: form.Pincode,
          Latitude: form.Latitude || editBase.Latitude,
          Longitude: form.Longitude || editBase.Longitude,
          ServiceIds: form.ServiceIds,
        },
      }),
    );
    if (updateBaseLocation.fulfilled.match(result)) {
      setEditBase(null);
      showToast("success", "Location updated successfully.");
    }
  };

  const showPageSkeleton = isLoading;
  const showEmptyState = !isLoading && bases.length === 0;
  const showContent = !isLoading && bases.length > 0;

  return (
    <div className="sl-page">
      <Container fluid className="px-4 py-4">

        {/* ── Toast ── */}
        {toast && (
          <div className={`sl-toast sl-toast--${toast.type}`}>
            {toast.type === "success" ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="8" fill="#10B981" opacity="0.15" stroke="#10B981" strokeWidth="1.5" />
                <path d="M5 9l3 3 5-5" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="8" fill="#EF4444" opacity="0.15" stroke="#EF4444" strokeWidth="1.5" />
                <path d="M6 6l6 6M12 6l-6 6" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
            <span>{toast.msg}</span>
          </div>
        )}

        {/* ── State 1: Loading ── */}
        {showPageSkeleton && <PageLoadingSkeleton />}

        {/* ── State 2: Empty ── */}
        {showEmptyState && (
          <EmptyLocationState
            allServices={allServices}
            isLoadingServices={isLoadingServices}
            onAddClick={() => setShowAddModal(true)}
          />
        )}

        {/* ── State 3: Content ── */}
        {showContent && (
          <>
            {/* Stat Cards */}
            <div className="sl-stats-row">
              {[
                { label: "Locations", value: summary.TotalLocations, img: totalDriverIcon },
                { label: "Active Services", value: summary.ActiveServicesCount, img: avDriverRatingIcon },
                { label: "In Catalogue", value: summary.InCatalogueCount, img: completedJobsIcon },
              ].map(({ label, value, img }) => (
                <div key={label} className="sl-stat-card">
                  <div className="sl-stat-text">
                    <span className="sl-stat-label">{label}</span>
                    <span className="sl-stat-value">{value}</span>
                  </div>
                  <img src={img} alt={label} className="sl-stat-icon-img" />
                </div>
              ))}
            </div>

            {/* Section Header */}
            <div className="sl-section-header">
              <div className="sl-section-left">
                <h2 className="sl-section-title">All Locations</h2>
                <div className="sl-search">
                  <input
                    type="text"
                    placeholder="Search location or service…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="sl-search-input"
                  />
                  <img src={searchIcon} alt="Search" className="sl-search-icon" />
                </div>
              </div>
              <button className="sl-add-btn" onClick={() => setShowAddModal(true)}>
                <AddIcon /> Add Location
              </button>
            </div>

            {/* Info Banner */}
            <div className="sl-info-banner">
              <img src={bulb_icon} alt="Tip" className="sl-bulb-icon" />
              <p>
                <strong>How it works:</strong> Each location has its own pricing
                &amp; availability. Service content (description, images,
                highlights, holiday uplift) is set once and shared across all
                locations automatically.
              </p>
            </div>

            {/* List */}
            <div className="sl-list">
              {filteredBases.length === 0 ? (
                <div className="sl-empty">
                  <div className="sl-empty-icon">📍</div>
                  <h3 className="sl-empty-title">No Results Found</h3>
                  <p className="sl-empty-text">No locations match your search.</p>
                </div>
              ) : (
                filteredBases.map((base) => (
                  <div key={base.BaseId} className="sl-card">
                    <div className="sl-card-main">
                      <div className="sl-pin-wrapper">
                        <img src={BaseLocation} alt="Base" className="sl-base-icon" />
                      </div>
                      <div className="sl-card-info">
                        <h3 className="sl-location-name">{base.BaseName}</h3>
                        <p className="sl-location-address">{base.Address}</p>
                      </div>
                      <ServiceTagRow
                        services={base.Services}
                        className="sl-tags--mobile-only"
                      />
                      <div className="sl-card-actions">
                        <span
                          className={`sl-active-badge${base.EnabledServicesCount === 0 ? " sl-active-badge--zero" : ""}`}
                        >
                          {base.EnabledServicesCount} Active
                        </span>
                        <button
                          className="sl-nav-btn"
                          onClick={() => setEditBase(base)}
                          title="Edit base"
                        >
                          <img src={BaseEditIcon} alt="Edit Base" width="18" height="18" />
                        </button>
                        <button
                          className="sl-nav-btn"
                          onClick={() => setDeleteTarget(base)}
                          title="Delete"
                        >
                          <img src={BaseDeleteIcon} alt="Delete" width="18" height="18" />
                        </button>
                        <button
                          className="sl-nav-btn"
                          onClick={() => navigate(`/service-location/${base.BaseId}`)}
                          title="View Details"
                        >
                          <ChevronRightIcon />
                        </button>
                      </div>
                    </div>
                    <ServiceTagRow
                      services={base.Services}
                      className="sl-tags--desktop-only"
                    />
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </Container>

      {/* ── Add Modal ── */}
      {showAddModal && (
        <LocationModal
          mode="add"
          allServices={allServices}
          isLoadingServices={isLoadingServices}
          isSubmitting={isCreating}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddSave}
        />
      )}

      {/* ── Edit Modal ── */}
      {editBase && (
        <LocationModal
          mode="edit"
          location={editBase}
          allServices={allServices}
          isLoadingServices={isLoadingServices}
          isSubmitting={isUpdating}
          onClose={() => setEditBase(null)}
          onSave={handleEditSave}
        />
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div className="sd-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="sd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sd-confirm-header">
              <button className="sd-confirm-close" onClick={() => setDeleteTarget(null)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 3L13 13M13 3L3 13"
                    stroke="#6366F1"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="sd-confirm-body">
              <div className="sd-confirm-icon">
                <img src={pauseConfirmIcon} alt="" style={{ width: 80, height: 80 }} />
              </div>
              <h4 className="sd-confirm-title">
                Are you sure you want <br /> to delete this location?
              </h4>
            </div>
            <div className="sd-confirm-footer">
              <button
                className="sd-confirm-btn sd-confirm-btn--outline"
                onClick={() => setDeleteTarget(null)}
                disabled={isUpdating}
              >
                CANCEL
              </button>
              <button
                className="sd-confirm-btn sd-confirm-btn--disable"
                onClick={handleDelete}
                disabled={isUpdating}
              >
                {isUpdating ? "..." : "DELETE"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceLocation;